/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import { LightTheme } from './light-theme/light-theme';
import { LightThemeMove } from './move-theme/light-theme-with-move';
import { DarkTheme } from './dark-theme/dark-theme';
import { DarkThemeMove } from './move-theme/dark-theme-with-move';
import createDarkTheme from './dark-theme/create-dark-theme';
import createLightTheme from './light-theme/create-light-theme';
import darkThemePrimitives from './dark-theme/primitives';
import lightThemePrimitives from './light-theme/primitives';
import type { Primitives } from './types';
import type { ColorTokens as StyleColors } from '../styles';
import { createBrandTheme,
// Default brand theme is the neutral base (no product), so it is re-exported
// unchanged.
createDefaultBrandThemeLight, createDefaultBrandThemeDark,
// Product brand themes — wrapped below to tag `product` for inference.
createEatsBrandThemeLight as createEatsBrandThemeLightBase, createEatsBrandThemeDark as createEatsBrandThemeDarkBase, createAIBrandThemeLight as createAIBrandThemeLightBase, createAIBrandThemeDark as createAIBrandThemeDarkBase, createMembershipBrandThemeLight as createMembershipBrandThemeLightBase, createMembershipBrandThemeDark as createMembershipBrandThemeDarkBase, createOffersBrandThemeLight as createOffersBrandThemeLightBase, createOffersBrandThemeDark as createOffersBrandThemeDarkBase } from '@uber/base-specs';
import type { ColorTheme } from '@uber/base-specs/src/generated/base-brand-themes';
import type { BaseProduct } from '../helpers/types';

/**
 * Tags a branded theme with its `product` so BaseProvider/ThemeProvider can
 * infer it (mirrors how the light/dark themes carry `themeMode`). Consumers
 * then don't have to pass a `product` prop when they build a theme with one of
 * the product brand creators.
 */
const withProduct = <T extends ColorTheme,>(theme: T, product: BaseProduct): T => ({
  ...theme,
  product
} as T & {
  product: BaseProduct;
});
export const createAIBrandThemeLight = <T extends ColorTheme,>(theme: T): T => withProduct(createAIBrandThemeLightBase(theme), 'ai');
export const createAIBrandThemeDark = <T extends ColorTheme,>(theme: T): T => withProduct(createAIBrandThemeDarkBase(theme), 'ai');
export const createEatsBrandThemeLight = <T extends ColorTheme,>(theme: T): T => withProduct(createEatsBrandThemeLightBase(theme), 'eats');
export const createEatsBrandThemeDark = <T extends ColorTheme,>(theme: T): T => withProduct(createEatsBrandThemeDarkBase(theme), 'eats');
export const createOffersBrandThemeLight = <T extends ColorTheme,>(theme: T): T => withProduct(createOffersBrandThemeLightBase(theme), 'offers');
export const createOffersBrandThemeDark = <T extends ColorTheme,>(theme: T): T => withProduct(createOffersBrandThemeDarkBase(theme), 'offers');
// Tagged pre-emptively: v2 product-token support may not exist yet, so this only
// emits `data-product="membership"` / feeds the JS path once tokens land.
export const createMembershipBrandThemeLight = <T extends ColorTheme,>(theme: T): T => withProduct(createMembershipBrandThemeLightBase(theme), 'membership');
export const createMembershipBrandThemeDark = <T extends ColorTheme,>(theme: T): T => withProduct(createMembershipBrandThemeDarkBase(theme), 'membership');
export { createDarkTheme, createLightTheme, createLightTheme as createTheme, LightTheme, LightThemeMove, lightThemePrimitives, DarkTheme, DarkThemeMove, darkThemePrimitives, DarkTheme as darkThemeOverrides,
// Brand themes - keep in sync with @uber/base-specs
createBrandTheme, createDefaultBrandThemeLight, createDefaultBrandThemeDark };
export * from './types';
/** @deprecated use ColorTokens instead. To be removed in future versions.*/
export type Colors = StyleColors;
/** @deprecated use Primitives instead. To be removed in future versions.*/
export type ThemePrimitives = Primitives;